import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Calendar, Clock, User, Mail, Phone, Sparkles, MessageCircle } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { WHATSAPP_LINK, openWhatsApp } from "@/data/site";

export default function PaymentSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const [data, setData] = useState(null);
    const [attempts, setAttempts] = useState(0);
    const [error, setError] = useState(false);

    useEffect(() => {
        if (!sessionId) return;
        let cancelled = false;
        let n = 0;
        const poll = async () => {
            n += 1;
            setAttempts(n);
            try {
                const { data: r } = await api.get(`/payments/status/${sessionId}`);
                if (cancelled) return;
                setData(r);
                if (r.payment_status === "paid") return;
                if (n < 30) setTimeout(poll, 2000);
            } catch {
                if (n < 5) setTimeout(poll, 2000);
                else setError(true);
            }
        };
        poll();
        return () => { cancelled = true; };
    }, [sessionId]);

    const paid = data?.payment_status === "paid";
    const b = data?.booking;

    return (
        <div data-testid="payment-success-page" className="pt-32 pb-20 min-h-screen">
            <div className="container-vv max-w-3xl">
                <div className="card-luxe text-center">
                    {paid ? (
                        <>
                            <div className="h-20 w-20 rounded-full bg-gold/15 grid place-items-center mx-auto mb-6">
                                <CheckCircle2 className="h-10 w-10 text-gold" />
                            </div>
                            <div className="label-eyebrow">Payment Confirmed</div>
                            <h1 className="font-serif text-3xl sm:text-4xl text-cream mt-4">Your session is booked!</h1>
                            <p className="mt-4 text-cream-muted max-w-lg mx-auto">
                                A confirmation has been logged. (For v1, email + WhatsApp + Calendar invites are simulated — real delivery available on request.)
                            </p>

                            {b && (
                                <div className="mt-10 text-left bg-ink-deep/40 border border-gold/15 rounded-xl p-6 space-y-3">
                                    <Detail icon={Sparkles} label="Service" value={b.service_name} />
                                    <Detail icon={Calendar} label="Date" value={b.date} />
                                    <Detail icon={Clock} label="Time" value={b.time_slot} />
                                    <Detail icon={User} label="Name" value={b.name} />
                                    <Detail icon={Mail} label="Email" value={b.email} />
                                    <Detail icon={Phone} label="Phone" value={b.phone} />
                                    <div className="pt-3 border-t border-gold/15 flex justify-between">
                                        <span className="text-cream-muted text-xs tracking-widest uppercase">Amount Paid</span>
                                        <span className="font-serif text-xl text-gold">{formatINR(b.price_inr)}</span>
                                    </div>
                                </div>
                            )}

                            <div className="mt-10 grid sm:grid-cols-2 gap-4 text-left">
                                <NotifyCard
                    icon={Mail}
                    title="Confirmation Email"
                    body={`Sent to ${b?.email || "your inbox"}`}
                />
                <NotifyCard
                    icon={MessageCircle}
                    title="WhatsApp"
                    body={`We'll message you at ${b?.phone || "your number"} (mocked in v1)`}
                />
                <NotifyCard
                    icon={Calendar}
                    title="Google Calendar"
                    body="Add the slot manually for now — invite emails coming soon."
                />
                                <NotifyCard
                                    icon={Sparkles}
                                    title="Next Steps"
                                    body="Dr. Tannu's team will reach out 24h before the session."
                                />
                            </div>

                            <div className="mt-10 flex justify-center gap-4">
                                <Link to="/" className="btn-outline-gold text-sm" data-testid="success-home-btn">Back to home</Link>
                                <a href={WHATSAPP_LINK} onClick={openWhatsApp} target="_blank" rel="noreferrer" className="btn-gold text-sm" data-testid="success-whatsapp-btn">Message us</a>
                            </div>
                        </>
                    ) : error ? (
                        <>
                            <h1 className="font-serif text-2xl text-cream">We couldn't verify your payment</h1>
                            <p className="mt-4 text-cream-muted">Please reach out via WhatsApp with your reference ID: <code className="text-gold">{sessionId}</code></p>
                            <a href={WHATSAPP_LINK} onClick={openWhatsApp} target="_blank" rel="noreferrer" className="btn-gold mt-6 inline-flex">Contact us</a>
                        </>
                    ) : (
                        <>
                            <div className="h-20 w-20 rounded-full border-2 border-gold/40 border-t-gold animate-spin mx-auto mb-6" />
                            <h1 className="font-serif text-2xl text-cream">Confirming your payment...</h1>
                            <p className="mt-3 text-cream-muted text-sm">Polling secure servers · attempt {attempts}/30</p>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

const Detail = ({ icon: Icon, label, value }) => (
    <div className="flex items-start gap-3">
        <Icon className="h-4 w-4 text-gold mt-0.5 flex-shrink-0" />
        <div>
            <div className="text-[10px] tracking-widest uppercase text-cream-muted">{label}</div>
            <div className="text-cream text-sm">{value || "—"}</div>
        </div>
    </div>
);

const NotifyCard = ({ icon: Icon, title, body }) => (
    <div className="border border-gold/15 rounded-lg p-4 bg-ink-deep/30">
        <div className="flex items-center gap-2 text-gold mb-2"><Icon className="h-4 w-4" /><span className="text-xs tracking-widest uppercase">{title}</span></div>
        <div className="text-xs text-cream-muted leading-relaxed">{body}</div>
    </div>
);
